import React from 'react';
import { ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function LessonsOfStrategy() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    back: 'Writing',
    badge: 'Five articles',
    title: 'The Lessons of Strategy',
    intro: `A strategy lets you say yes to one approach and no to most others.

That is the whole definition. If what you are calling a strategy does not let you do that - if it is a list of priorities that somehow includes everything, a vision statement that commits you to nothing, a set of goals dressed up in a PowerPoint deck - then you do not have a strategy. You have a wish list with better production values.

Most organisations find this uncomfortable because most organisations have never actually made the choices that strategy requires. Not because they lack intelligence or ambition. But because real strategic choice means accepting tradeoffs that nobody in the room wants to be responsible for. It is easier to write something that sounds strategic and move on. And so they do.

The result is what Richard Rumelt calls bad strategy - not merely weak or incomplete, but actively counterproductive, because it creates the impression that choices have been made when they haven't, and gives organisations confidence they haven't earned.

This series looks at what strategy actually is, why the gap between real strategy and strategy theatre is so persistent, and what a genuine alternative looks like for organisations that do not have a McKinsey budget but still need to make coherent decisions about where to go and how to get there.`,
    articles: [{
      n: '01',
      title: 'What Strategy Actually Is',
      desc: "Starting with Rumelt's kernel and the basic test: does this allow you to say no? If not, it is not a strategy.",
      live: false
    }, {
      n: '02',
      title: 'The Coherence Problem',
      desc: 'Why internal consistency is necessary but not sufficient - and why most strategy processes optimise for the wrong thing.',
      live: false
    }, {
      n: '03',
      title: 'The Moving Landscape',
      desc: 'Wardley mapping and the situational awareness question: where are you, and where is everything else? Strategy requires a map.',
      live: false
    }, {
      n: '04',
      title: 'Outside In',
      desc: 'Impact mapping and the inversion that changes how you think about your business: starting from the world you want to create, not the products you want to sell.',
      live: false
    }, {
      n: '05',
      title: 'The Ongoing Practice',
      desc: 'Strategy is not an event. It is a discipline. What a real strategic practice looks like for organisations that cannot afford the McKinsey version.',
      live: false
    }],
    substackCta: 'Follow on Substack - series publishing soon',
    relatedTitle: 'Related',
    related: [{
      label: 'Strategy Workshop',
      href: '/services/workshops/strategy'
    }]
  };
  const is = {
    back: 'Skrif',
    badge: 'Fimm greinar',
    title: 'Lærdómar um stefnu',
    intro: `Stefnumótun eða óskalisti?

Stefna gerir þér kleift að segja já við einni nálgun og nei við flestum öðrum. Það er öll skilgreiningin. Ef það sem þú kallar stefnu gerir þér þetta ekki kleift – ef það er listi yfir forgangsverkefni sem einhvern veginn nær yfir allt, framtíðarsýn sem skuldbindur þig til einskis, eða markmiðapakki í fínni PowerPoint kynningu – þá ertu ekki með stefnu. Þú ert með óskalista í flottum umbúðum.

Flestum skipulagsheildum þykir þetta óþægilegt vegna þess að fæstar þeirra hafa í raun tekið þær ákvarðanir sem stefnumótun krefst. Ekki vegna þess að þær skorti gáfur eða metnað, heldur vegna þess að raunveruleg stefnumótandi val fela í sér fórnarskipti sem enginn í herberginu vill bera ábyrgð á. Það er auðveldara að skrifa eitthvað sem hljómar „strategískt" og halda áfram. Og það er einmitt það sem þær gera.

Niðurstaðan er það sem Richard Rumelt kallar slæma stefnu – ekki einungis veika eða ófullkomna, heldur beinlínis skaðlega. Hún gefur nefnilega þá blekkingu að ákvarðanir hafi verið teknar þegar svo er ekki, og veitir fyrirtækjum sjálfstraust sem þau hafa ekki unnið fyrir.

Í þessari seríu er skoðað hvað stefnumótun er í raun, hvers vegna bilið á milli raunverulegrar stefnu og „stefnumótunarleikhúss" er svona lífseigt, og hvernig raunverulegur valkostur lítur út fyrir þau sem eru ekki með fjárhagsáætlun á borð við McKinsey, en þurfa samt sem áður að taka rökréttar ákvarðanir um hvert skal stefna og hvernig eigi að komast þangað.`,
    articles: [{
      n: '01',
      title: 'Hvað stefna er í raun',
      desc: 'Byrjað með Rumelt-kjarnanum og grunnprófinu: leyfir þetta þér að segja nei? Ef ekki, er þetta ekki stefna.',
      live: false
    }, {
      n: '02',
      title: 'Samræmisvandinn',
      desc: 'Hvers vegna innri samræmi er nauðsynlegt en ekki nægjanlegt - og hvers vegna flestar stefnuferlar fínstilla ranga hlutinn.',
      live: false
    }, {
      n: '03',
      title: 'Hreyfanlegt landslag',
      desc: 'Wardley-kortlagning og málið um stöðugreiningu: hvar ertu, og hvar er allt annað? Stefna þarf kort.',
      live: false
    }, {
      n: '04',
      title: 'Utan frá inn',
      desc: 'Áhrif-kortlagning og snúningurinn sem breytir hvernig þú hugar um reksturinn: byrjað í heiminum sem þú vilt skapa, ekki vörurnar sem þú vilt selja.',
      live: false
    }, {
      n: '05',
      title: 'Lífleg iðkun',
      desc: 'Stefna er ekki atburður. Hún er agi. Hvernig raunveruleg stefnuiðkun lítur út fyrir stofnanir sem geta ekki leyft sér McKinsey-útgáfuna.',
      live: false
    }],
    substackCta: 'Fylgjast á Substack - röðin kemur fljótlega',
    relatedTitle: 'Tengt',
    related: [{
      label: 'Stefnumót',
      href: '/services/workshops/strategy'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2173">
      <div className="pt-12 pb-24" data-id="element-2174">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2175">
          <Animate data-id="element-2176">
            <Link to="/writing" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal mb-8 transition-colors" data-id="element-2177">
              <ChevronRight size={14} className="rotate-180" data-id="element-2178" />
              {c.back}
            </Link>
          </Animate>
          <Animate data-id="element-2179">
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal font-medium rounded-full text-xs mb-4" data-id="element-2180">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-2181">
              {c.title}
            </h1>
            <div className="prose prose-lg text-gray-500 leading-relaxed mb-12 space-y-4" data-id="element-2182">
              {c.intro.split('\n\n').map((para, i) => <p key={i} data-id="element-2183">{para}</p>)}
            </div>
          </Animate>
          <div className="space-y-4 mb-12" data-id="element-2184">
            {c.articles.map((a, i) => <Animate key={a.n} delay={i * 0.05} data-id="element-2185">
                <Card className="p-6 border border-gray-100 opacity-70" data-id="element-2186">
                  <div className="flex items-start gap-4" data-id="element-2187">
                    <span className="text-2xl font-bold text-gray-200 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2188">
                      {a.n}
                    </span>
                    <div className="flex-1" data-id="element-2189">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2190">
                        <h2 className="font-bold text-charcoal" data-id="element-2191">{a.title}</h2>
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium" data-id="element-2192">
                          Coming soon
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2193">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                </Card>
              </Animate>)}
          </div>
          <Animate data-id="element-2194">
            <Card className="p-6 bg-teal/5 border-teal/20 border text-center mb-12" data-id="element-2195">
              <p className="text-gray-600 mb-4" data-id="element-2196">{c.substackCta}</p>
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2197">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2199" />} data-id="element-2198">
                  Substack
                </Button>
              </a>
            </Card>
          </Animate>
          <Animate data-id="element-2200">
            <div data-id="element-2201">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2202">
                {c.relatedTitle}
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-2203">
                {c.related.map(r => <Link key={r.href} to={r.href} data-id="element-2204">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-2206" />} data-id="element-2205">
                      {r.label}
                    </Button>
                  </Link>)}
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </PageTransition>;
}